/**
 * The patient's dashboard, mockup 4 of 23 September 2026.
 *
 * Brian's own instructions for this screen, verbatim: "Show patient GOAL in
 * red like in the image, then below Show the three score rings: Metabolic,
 * Inflammation, Liver Health. Immediately underneath those scores, show the
 * patient's current Treatment Plan. Remove Overall Score. Remove 'What Your
 * Results Mean.' Remove Personalized Recommendations. Remove 'How This
 * Compares With Last Time.' Keep the patient experience very clean and
 * simple. The priority should be: Scores -> Treatment Plan -> Progress/Labs
 * -> Clinical Twin information."
 *
 * So, top to bottom: the greeting, the red goal, the three rings, the plan,
 * progress, the four quick actions, and the Clinical Twin card. Every number
 * is a stored measurement; where one does not exist the card says so plainly
 * instead of drawing a placeholder.
 */

import { Link } from 'react-router-dom';
import { usePortal } from '../../lib/portal';
import {
  Card,
  CardHead,
  DISPLAY,
  DISPLAY_MID,
  IconTile,
  PortalError,
  PortalLoading,
  TONE,
  ViewLink,
  formatLbs,
  formatMonthYear,
  formatNumber,
  usePortalQuery,
} from './shell';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BarsLine,
  Book,
  Bullseye,
  Calendar,
  ChatBubble,
  Chevron,
  Flask,
  LeafSolid,
  Molecule,
  Syringe,
  TriangleDown,
  TriangleUp,
  Users,
} from './icons';
import { ScanDueCard } from './BodyScan';
import { DoseStepNotice } from './DoseSteps';

/* ------------------------------------------------------------------ *
 * What the API sends (GET /portal/home)
 * ------------------------------------------------------------------ */

export interface Ring {
  key: 'metabolic' | 'inflammation' | 'liver';
  label: string;
  score: number | null;
  /** Points moved since the previous released report. Null on the first. */
  movement: number | null;
}

export interface DashboardTreatment {
  therapyId: string;
  name: string;
  category: string;
  route: string | null;
  useOrIndication: string | null;
  goalTags: string[];
}

export interface Recommendation {
  group: 'nutrition' | 'activity' | 'sleep';
  title: string;
  body: string;
}

export interface PatientHome {
  patient: { firstName: string; lastName: string };
  memberActive: boolean;
  goal: { headline: string; primaryGoal: string; targetValue: number | null; targetUnit: string | null } | null;
  rings: Ring[];
  latestReportId: string | null;
  plan: { startedAt: string | null; treatments: DashboardTreatment[] } | null;
  body: {
    currentWeightLb: number | null;
    currentAt: string | null;
    changeSinceStartLb: number | null;
    totalLossPct: number | null;
    goalProgressPct: number | null;
    monthsOnProgram: number | null;
    programStartedAt: string | null;
  };
  twin: { hasPlan: boolean; cohortSize: number | null; sufficient: boolean };
  recommendations: Recommendation[];
  hasTreatmentPlan: boolean;
}

/* ------------------------------------------------------------------ *
 * Score rings
 * ------------------------------------------------------------------ */

/**
 * Each ring's gradient, sampled clockwise from twelve o'clock around the
 * mockup's rings: the dark end starts the arc, the light end finishes it.
 * Positions are degrees round the whole circle.
 */
type Stop = [number, string];

const RINGS: Record<
  Ring['key'],
  { label: string; stops: Stop[]; pill: string; pillText: string; track: string }
> = {
  metabolic: {
    label: 'Metabolism',
    stops: [
      [0, '#025441'],
      [30, '#014332'],
      [165, '#024a38'],
      [182, '#0e5c45'],
      [205, '#3f9170'],
      [232, '#72d1a5'],
      [270, '#75d6a8'],
      [320, '#a0d8bf'],
      [356, '#b6ddcb'],
    ],
    pill: '#e4f1ea',
    pillText: '#0a3d30',
    track: '#e8f1ec',
  },
  inflammation: {
    label: 'Inflammation',
    stops: [
      [0, '#b67553'],
      [30, '#a96441'],
      [130, '#ab6542'],
      [152, '#b77157'],
      [182, '#c4876d'],
      [225, '#c98e5f'],
      [300, '#cc9163'],
      [356, '#d2986f'],
    ],
    pill: '#f2eae2',
    pillText: '#7c3c1b',
    track: '#f4ede6',
  },
  liver: {
    label: 'Liver Health',
    stops: [
      [0, '#408dc4'],
      [140, '#3c8dc5'],
      [165, '#4e9fd6'],
      [182, '#60a8dc'],
      [215, '#66b7ee'],
      [300, '#70bbef'],
      [356, '#7bc1ef'],
    ],
    pill: '#e3f0f8',
    pillText: '#246998',
    track: '#e8f1f8',
  },
};

function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function colourAt(stops: Stop[], angle: number): string {
  const after = stops.findIndex(([at]) => at >= angle);
  if (after <= 0) return after === 0 ? stops[0]![1] : stops[stops.length - 1]![1];
  const [a0, c0] = stops[after - 1]!;
  const [a1, c1] = stops[after]!;
  const t = (angle - a0) / (a1 - a0);
  const [r0, g0, b0] = hexToRgb(c0);
  const [r1, g1, b1] = hexToRgb(c1);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `rgb(${mix(r0, r1)},${mix(g0, g1)},${mix(b0, b1)})`;
}

function arcPath(cx: number, cy: number, r: number, from: number, to: number): string {
  const point = (deg: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return `${(cx + r * Math.cos(rad)).toFixed(3)} ${(cy + r * Math.sin(rad)).toFixed(3)}`;
  };
  const large = to - from > 180 ? 1 : 0;
  return `M ${point(from)} A ${r} ${r} 0 ${large} 1 ${point(to)}`;
}

/**
 * A closed ring with a gradient stroke and a hairline gap at twelve o'clock,
 * as drawn: 90px across, an 8.5px band, flat ends.
 *
 * The arc length is the score, so a 60 draws three fifths of a ring; the
 * mockup's scores are all in the high nineties, which is why its rings look
 * full. SVG has no conic gradient, so the band is laid down in short
 * overlapping segments, each in the colour of its angle.
 */
export function ScoreRing({ ringKey, score }: { ringKey: Ring['key']; score: number | null }) {
  const size = 90;
  const band = 8.5;
  const r = (size - band) / 2;
  const c = size / 2;
  const gap = 4;
  const start = gap / 2;
  const available = 360 - gap;
  const value = score === null ? 0 : Math.min(100, Math.max(0, score));
  const end = start + (available * value) / 100;
  const spec = RINGS[ringKey];

  const segments: Array<{ d: string; colour: string }> = [];
  const step = 3;
  for (let at = start; at < end - 0.01; at += step) {
    const to = Math.min(end, at + step + 0.8);
    segments.push({ d: arcPath(c, c, r, at, to), colour: colourAt(spec.stops, at + step / 2) });
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={score === null ? `${spec.label}, not scored yet` : `${spec.label}, ${score} out of 100`}
    >
      {value < 100 && (
        <path d={arcPath(c, c, r, end, 360 - start)} fill="none" stroke={spec.track} strokeWidth={band} />
      )}
      {segments.map((segment, index) => (
        <path key={index} d={segment.d} fill="none" stroke={segment.colour} strokeWidth={band} />
      ))}
      <text
        x={c}
        y={c + 9.4}
        textAnchor="middle"
        fill={TONE.heading}
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 26.5,
          fontWeight: 560,
          letterSpacing: '-0.02em',
          fontVariationSettings: '"opsz" 60',
        }}
      >
        {score ?? '-'}
      </text>
    </svg>
  );
}

/** "UP 18" under a ring, tinted to that ring. Hidden when there is nothing to compare. */
export function MovePill({ ringKey, movement }: { ringKey: Ring['key']; movement: number | null }) {
  if (movement === null) return <span className="block h-[23px]" aria-hidden="true" />;
  const spec = RINGS[ringKey];
  const label = movement === 0 ? 'NO CHANGE' : `${movement > 0 ? 'UP' : 'DOWN'} ${Math.abs(movement)}`;
  return (
    <span
      className="inline-flex h-[23px] items-center gap-[8px] rounded-[7px] px-[12px] text-[11.5px] font-semibold uppercase leading-none tracking-[0.01em]"
      style={{ background: spec.pill, color: spec.pillText }}
      aria-label={movement === 0 ? 'No change since your last report' : `${movement > 0 ? 'Up' : 'Down'} ${Math.abs(movement)} since your last report`}
    >
      {movement > 0 && <TriangleUp size={9} />}
      {movement < 0 && <TriangleDown size={9} />}
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Treatment icons
 * ------------------------------------------------------------------ */

/**
 * Which drawing a treatment row gets, from the treatment's own category, the
 * way mockup 4 assigns them: the GLP-1 is the syringe, a peptide is the
 * molecule, and vitamins and everything else are the leaf. (Mockup 4 draws
 * B12, an intramuscular injection, with the leaf, so the route alone does not
 * decide it.)
 */
export function planIcon(category: string): 'syringe' | 'molecule' | 'leaf' {
  const value = category.toLowerCase();
  if (value.includes('glp')) return 'syringe';
  if (value.includes('peptide')) return 'molecule';
  return 'leaf';
}

export function PlanIcon({ category, size = 21 }: { category: string; size?: number }) {
  const kind = planIcon(category);
  if (kind === 'syringe') return <Syringe size={size} strokeWidth={1.9} />;
  if (kind === 'molecule') return <Molecule size={size} strokeWidth={1.9} />;
  return <LeafSolid size={size} />;
}

/** One inset row on the plan card: icon, name, what it is for, chevron. */
export function PlanRow({ treatment, to }: { treatment: DashboardTreatment; to: string }) {
  return (
    <Link
      to={to}
      className="flex h-[41px] items-center gap-[14px] rounded-[7px] pl-[3px] pr-[12px] transition-colors hover:brightness-[0.985]"
      style={{ background: TONE.row }}
    >
      <IconTile size={35}>
        <PlanIcon category={treatment.category} />
      </IconTile>
      <span className="min-w-0 flex-1">
        <span
          className="block truncate font-display text-[12px] font-semibold leading-[15px]"
          style={{ color: TONE.heading, fontVariationSettings: '"opsz" 24' }}
        >
          {treatment.name}
        </span>
        {treatment.useOrIndication && (
          <span className="mt-[1px] block truncate text-[10px] leading-[13px]" style={{ color: '#383f4c' }}>
            {treatment.useOrIndication}
          </span>
        )}
      </span>
      <span className="shrink-0" style={{ color: '#323548' }}>
        <Chevron size={15} strokeWidth={1.9} />
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

function greeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good Morning,';
  if (hour < 18) return 'Good Afternoon,';
  return 'Good Evening,';
}

export function PortalHome() {
  const { me } = usePortal();
  const { data, isLoading, error, refetch } = usePortalQuery<PatientHome>('home', '/home');
  const messages = usePortalQuery<{ unreadThreads: number }>('messages-home', '/messages');

  if (isLoading) return <PortalLoading />;
  if (error) {
    return (
      <PortalError
        message={error instanceof Error ? error.message : 'Could not load your dashboard'}
        retry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const reportLink = data.latestReportId ? `/portal/reports/${data.latestReportId}` : '/portal/progress';

  return (
    <div className="space-y-[8px]">
      {/* 1. Greeting, and membership when it is running. */}
      <div className="flex items-center justify-between gap-3 px-1 pt-[5px]">
        <div className="min-w-0">
          <p
            className="font-display text-[15px] leading-[1.1]"
            style={{ color: TONE.heading, fontWeight: 480, fontVariationSettings: '"opsz" 24' }}
          >
            {greeting()}
          </p>
          <h1
            className="mt-[1px] truncate font-display text-[26px] font-semibold leading-[1.0]"
            style={{ ...DISPLAY, color: TONE.heading, letterSpacing: '-0.012em' }}
          >
            {data.patient.firstName} {data.patient.lastName}
          </h1>
        </div>
        {data.memberActive && (
          <span
            className="inline-flex h-[21px] shrink-0 items-center gap-[6px] rounded-full pl-[8px] pr-[10px] text-[11px] font-medium leading-none"
            style={{ background: '#e7f1ec', color: TONE.forestText }}
          >
            <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-forest" />
            Active Member
          </span>
        )}
      </div>

      {/* Something the patient needs to do now: a dose change to confirm, a body scan due. */}
      <DoseStepNotice />
      <ScanDueCard />

      {/* 2. The goal, in red, first. */}
      {data.goal ? (
        <Link
          to="/portal/plan"
          className="flex min-h-[51px] items-center gap-[11px] rounded-[9px] border pb-[7px] pl-[13px] pr-[10px] pt-[8px]"
          style={{ background: 'linear-gradient(90deg, #fde3e3 0%, #fde8e7 100%)', borderColor: '#f6d3d3' }}
        >
          <span className="shrink-0 text-alert">
            <Bullseye size={34} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10.5px] font-semibold uppercase leading-none tracking-[0.07em] text-alert">
              Your goal
            </span>
            <span
              className="-mt-[1px] block truncate font-display text-[25px] font-semibold leading-[1.02] text-alert"
              style={{ ...DISPLAY, letterSpacing: '-0.01em' }}
            >
              {data.goal.headline}
            </span>
          </span>
          <span className="shrink-0 text-alert opacity-90">
            <Chevron size={16} strokeWidth={1.8} />
          </span>
        </Link>
      ) : (
        <div
          className="flex items-center gap-[15px] rounded-[9px] border px-[14px] py-[12px]"
          style={{ background: '#fdf0ef', borderColor: '#f6dcdc' }}
        >
          <span className="shrink-0 text-alert opacity-70">
            <Bullseye size={28} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10.5px] font-semibold uppercase leading-none tracking-[0.07em] text-alert">
              Your goal
            </span>
            <span className="mt-[5px] block text-[13px] leading-snug" style={{ color: TONE.body }}>
              Your clinician will set your goal with you at your next visit.
            </span>
          </span>
        </div>
      )}

      {/* 3. The three scores. */}
      <Card className="px-[13px] pb-[11px] pt-[9px]">
        <CardHead
          label="Your clinical picture"
          link={data.latestReportId ? <ViewLink to={reportLink}>View Details</ViewLink> : undefined}
        />
        {data.rings.length === 0 ? (
          <p className="py-8 text-center text-[13px] leading-relaxed" style={{ color: TONE.body }}>
            Your scores appear here once your first blood draw has been reviewed and released by your
            clinician.
          </p>
        ) : (
          <div className="mt-[8px] grid grid-cols-3">
            {data.rings.map((ring) => (
              <div key={ring.key} className="flex flex-col items-center">
                <ScoreRing ringKey={ring.key} score={ring.score} />
                <p className="mt-[4px] text-[13px] font-medium leading-tight" style={{ color: TONE.heading }}>
                  {RINGS[ring.key].label}
                </p>
                <span className="mt-[6px] flex">
                  <MovePill ringKey={ring.key} movement={ring.movement} />
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 4. The plan, immediately under the scores. */}
      <Card className="px-[13px] pb-[8px] pt-[9px]">
        <CardHead
          label="Your treatment plan"
          link={data.plan ? <ViewLink to="/portal/plan">View Full Plan</ViewLink> : undefined}
        />
        {data.plan && data.plan.treatments.length > 0 ? (
          <>
            <p className="mt-[4px] text-[12.5px] leading-snug" style={{ color: '#303a4b' }}>
              Personalized for your goals and lab results.
            </p>
            <div className="mt-[8px] space-y-[6px]">
              {data.plan.treatments.map((treatment) => (
                <PlanRow key={treatment.therapyId} treatment={treatment} to="/portal/plan" />
              ))}
            </div>
          </>
        ) : (
          <p className="pb-3 pt-[10px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
            Your clinician has not released a treatment plan yet. When they do, it appears here and you
            will get a message.
          </p>
        )}
      </Card>

      {/* 5. Progress, from body composition scans and the programme dates. */}
      <ProgressCard body={data.body} goal={data.goal} />

      {/* 6. Quick actions. */}
      <nav aria-label="Quick actions" className="grid grid-cols-[87fr_85fr_92fr_86fr] gap-[6px]">
        <QuickTile to={reportLink} label="View Labs" icon={<Flask size={23} strokeWidth={1.8} />} accent />
        <QuickTile to="/portal/progress" label="My Progress" icon={<BarsLine size={23} strokeWidth={1.7} />} />
        {/* MS-001: the tile shows how many conversations have an unread care-team reply. */}
        <QuickTile
          to="/portal/messages"
          label="Message Care Team"
          icon={<ChatBubble size={23} strokeWidth={1.7} />}
          badge={messages.data?.unreadThreads ?? 0}
        />
        <QuickTile to="/portal/resources" label="Resources" icon={<Book size={23} strokeWidth={1.7} />} />
      </nav>

      {/* 7. Clinical Twin, last in Brian's order. */}
      <TwinTeaserCard twin={data.twin} />

      {me?.renewalDue && (
        <Card className="px-[13px] py-[12px]">
          <CardHead label="Your programme" />
          <p className="mt-[8px] text-[13px] leading-snug" style={{ color: TONE.body }}>
            Your monitoring cycle is coming to an end. You can confirm your renewal from your account.
          </p>
          <Link
            to="/portal/account"
            className="mt-[10px] inline-flex h-[34px] items-center gap-2 rounded-[8px] bg-forest px-4 text-[13px] font-semibold text-white"
          >
            Go to account
            <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Progress card
 * ------------------------------------------------------------------ */

function ProgressCard({ body, goal }: { body: PatientHome['body']; goal: PatientHome['goal'] }) {
  const lost = body.totalLossPct;
  const gained = lost !== null && lost < 0;

  return (
    <Card className="px-[13px] pb-[11px] pt-[9px]">
      <CardHead label="Your progress" link={<ViewLink to="/portal/progress">View Trends</ViewLink>} />
      <div className="mt-[14px] grid grid-cols-[97fr_140fr_118fr]">
        {/* Current weight, and the change since the first scan. */}
        <div className="min-w-0 pr-2">
          <p className="whitespace-nowrap text-[10.5px] leading-tight" style={{ color: TONE.body }}>
            Current Weight
          </p>
          <p
            className="mt-[6px] whitespace-nowrap font-display text-[22px] font-semibold leading-none"
            style={{ ...DISPLAY, color: TONE.heading }}
          >
            {body.currentWeightLb !== null ? `${formatLbs(body.currentWeightLb)} lbs` : '-'}
          </p>
          {body.changeSinceStartLb !== null && body.changeSinceStartLb !== 0 ? (
            <>
              <p
                className="mt-[9px] flex items-center gap-[4px] text-[13px] font-semibold leading-none"
                style={{ color: body.changeSinceStartLb < 0 ? TONE.forestText : TONE.body }}
              >
                {body.changeSinceStartLb < 0 ? (
                  <ArrowDown size={14} strokeWidth={2.2} />
                ) : (
                  <ArrowUp size={14} strokeWidth={2.2} />
                )}
                {formatLbs(Math.abs(body.changeSinceStartLb))} lbs
              </p>
              <p className="mt-[5px] pl-[16px] text-[10.5px] leading-none" style={{ color: TONE.body }}>
                since starting
              </p>
            </>
          ) : (
            <p className="mt-[9px] text-[10.5px] leading-snug" style={{ color: TONE.faint }}>
              {body.currentWeightLb === null ? 'After your first scan' : 'Change shows after your next scan'}
            </p>
          )}
        </div>

        {/* Total loss as a share of the starting weight, and the goal bar. */}
        <div className="min-w-0 border-l pl-[17px] pr-3" style={{ borderColor: '#e6e8e7' }}>
          <p className="whitespace-nowrap text-[10.5px] leading-tight" style={{ color: TONE.body }}>
            {gained ? 'Total Body Weight Change' : 'Total Body Weight Loss'}
          </p>
          <p
            className="mt-[6px] font-display text-[22px] font-semibold leading-none"
            style={{ ...DISPLAY, color: TONE.heading }}
          >
            {lost === null ? '-' : `${gained ? '+' : ''}${formatNumber(Math.abs(lost), 1)}%`}
          </p>
          {body.goalProgressPct !== null ? (
            <div
              className="mt-[11px] h-[10px] overflow-hidden rounded-full"
              style={{ background: '#e3e8e6' }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={body.goalProgressPct}
              aria-label={`${body.goalProgressPct}% of the way to your goal${goal ? `, ${goal.headline}` : ''}`}
              title={`${body.goalProgressPct}% of the way to your goal`}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(body.goalProgressPct, body.goalProgressPct > 0 ? 6 : 0)}%`,
                  background: 'linear-gradient(90deg, #0b5345 0%, #0a4a3c 100%)',
                }}
              />
            </div>
          ) : (
            <p className="mt-[9px] text-[10.5px] leading-snug" style={{ color: TONE.faint }}>
              {lost === null ? 'Needs two scans' : ''}
            </p>
          )}
        </div>

        {/* Whole months since enrolment. */}
        <div className="min-w-0 border-l pl-[13px]" style={{ borderColor: '#e6e8e7' }}>
          <p className="whitespace-nowrap text-[10.5px] leading-tight" style={{ color: TONE.body }}>
            Time on Program
          </p>
          <p
            className="mt-[6px] whitespace-nowrap font-display text-[22px] font-semibold leading-none"
            style={{ ...DISPLAY, color: TONE.heading }}
          >
            {body.monthsOnProgram === null
              ? '-'
              : body.monthsOnProgram === 0
                ? 'New'
                : `${body.monthsOnProgram} ${body.monthsOnProgram === 1 ? 'month' : 'months'}`}
          </p>
          {body.programStartedAt && (
            <p className="mt-[8px] flex items-center gap-[5px] text-[10px] leading-none" style={{ color: TONE.body }}>
              <span className="shrink-0" style={{ color: '#2a3342' }}>
                <Calendar size={16} strokeWidth={1.5} />
              </span>
              <span className="whitespace-nowrap">Started {formatMonthYear(body.programStartedAt)}</span>
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Quick actions and the Clinical Twin card
 * ------------------------------------------------------------------ */

function QuickTile({ to, label, icon, accent = false, badge = 0 }: { to: string; label: string; icon: React.ReactNode; accent?: boolean; badge?: number }) {
  return (
    <Link
      to={to}
      className="relative flex h-[54px] flex-col items-center justify-between rounded-[7px] border bg-white px-1 pb-[8px] pt-[8px] transition-colors hover:bg-[#fbfbfa]"
      style={{ borderColor: TONE.line }}
    >
      {badge > 0 && (
        <span className="absolute right-[6px] top-[4px] grid h-[18px] min-w-[18px] place-items-center rounded-full bg-alert px-1 text-[10.5px] font-semibold leading-none text-white" aria-label={`${badge} unread`}>
          {badge}
        </span>
      )}
      <span style={{ color: accent ? 'var(--forest)' : '#2a3342' }}>{icon}</span>
      <span className="whitespace-nowrap text-[10px] leading-none tracking-[-0.005em]" style={{ color: '#2e3746' }}>
        {label}
      </span>
    </Link>
  );
}

function TwinTeaserCard({ twin }: { twin: PatientHome['twin'] }) {
  return (
    <Card className="px-[13px] pb-[13px] pt-[11px]">
      <CardHead label="Your clinical twin" />
      <div className="mt-[11px] flex items-center gap-[12px]">
        <IconTile size={40} round>
          <Users size={22} strokeWidth={1.8} />
        </IconTile>
        <div className="min-w-0">
          {twin.hasPlan && twin.sufficient && twin.cohortSize !== null ? (
            <p
              className="font-display text-[19px] font-semibold leading-tight"
              style={{ ...DISPLAY_MID, color: TONE.heading }}
            >
              {formatNumber(twin.cohortSize)} similar patients
            </p>
          ) : (
            <p
              className="font-display text-[17px] font-semibold leading-tight"
              style={{ ...DISPLAY_MID, color: TONE.heading }}
            >
              {twin.hasPlan ? 'Limited MCOS data' : 'Not available yet'}
            </p>
          )}
          <p className="mt-[3px] text-[12px] leading-snug" style={{ color: TONE.body }}>
            {twin.hasPlan
              ? twin.sufficient
                ? 'See what happened to patients like you on the same plan.'
                : 'Not enough patients have completed your exact plan to compare yet.'
              : 'Once your clinician has chosen your plan, you can compare with patients like you.'}
          </p>
        </div>
      </div>
      <Link
        to="/portal/health"
        className="mt-[12px] flex h-[38px] items-center justify-center gap-2 rounded-[8px] bg-forest text-[13px] font-semibold text-white transition-colors hover:bg-forest-deep"
      >
        Explore Your Clinical Twin
        <ArrowRight size={15} strokeWidth={2} />
      </Link>
    </Card>
  );
}
